'use client';

import type React from 'react';
import type { ContactClickstreamContext } from '@/src/components/features/property/ContactForm/types';
import { Calendar, ExternalLink, Mail } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { LucideIcon } from '@/src/components/LucideIcon';
import { useContactFormAction } from '@/src/components/features/property/ContactForm/useContactFormAction';
import { useOptionalHostedTourAction, useTourAction } from '@/src/components/features/property/TourForm';
import { useApplyAction } from '@/src/components/features/property/ApplyForm';
import { useRecentlyContactedListing } from '@/src/lib/hooks/query/useRecentlyContactedListing';
import { useCurrentUser } from '@/src/lib/hooks/query';
import { isWaitlistedListing } from '@/src/components/features/property/lib/waitlist';
import { useListingCTAAvailability } from './useListingCTAAvailability';

export type ListingCTAId = 'contact' | 'tour' | 'apply';

export type ListingCTA = {
  id: ListingCTAId;
  label: string;
  ariaLabel: string;
  icon: React.ReactNode;
  /** An outbound CTA renders as a link that opens in a new tab. */
  href?: string;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  emphasis: 'filled' | 'outlined';
  disabled?: boolean;
  ariaBusy?: boolean;
};

type UseListingCTAsResult = {
  ctas: Array<ListingCTA>;
  overlays: React.ReactNode;
};

/**
 * useListingCTAs — computes the HDP action-bar CTAs (contact / tour / apply)
 * for a listing and returns the overlay nodes the caller must render.
 *
 * Every surface renders them in this order: Message, Tour, then Apply. Tour is
 * only included when the listing has instant tour providers. Apply is only included for listings accepting Zillow applications, matching the
 * legacy `shouldShowApplyButton` rentalApplicationStatus === "accepted" gate.
 */
export const useListingCTAs = (
  listing: PropertyDetailListing | null | undefined,
  triggerLocation = 'home_details_halo',
  triggerObject = 'home_details_component|summary',
  contactApplyContext: ContactClickstreamContext = { triggerLocation, triggerObject },
): UseListingCTAsResult => {
  const t = useTranslations('hdp');
  const { data: currentUser } = useCurrentUser();
  const recentlyContacted = useRecentlyContactedListing(listing, {
    loggedIn: currentUser?.loggedIn === true,
    email: currentUser?.data?.email,
  });
  const availability = useListingCTAAvailability(listing);
  const { openContactForm, contactFormOverlay, identityResolved } = useContactFormAction(
    listing,
    contactApplyContext.triggerLocation,
    contactApplyContext.triggerObject,
  );
  // Prefers the page's shared wizard where there is one, so a tour opened from a button here and
  // one opened by `book_tour` are the same wizard rather than two stacked modals. Both HDP routes
  // (pad and building) host one. Falls back to its own instance elsewhere - ListingPage,
  // MediaCenter, listing cards - which keeps those exactly as they were.
  //
  // A host serves one listing, and the hook returns null for a caller showing a different one, so
  // a unit overlay under the building host gets its own wizard rather than the building's.
  const hostedTour = useOptionalHostedTourAction(triggerLocation, listing);
  const ownTour = useTourAction(listing, triggerLocation);
  const { openTour, isTourReady } = hostedTour ?? ownTour;
  const tourOverlay = hostedTour ? null : ownTour.tourOverlay;
  const { applyLink, openApply, applyOverlay } = useApplyAction(listing, contactApplyContext.triggerLocation);

  const overlays = (
    <>
      {contactFormOverlay}
      {tourOverlay}
      {applyOverlay}
    </>
  );

  if (!listing || listing.active === false) {
    return { ctas: [], overlays };
  }

  const isWaitlisted = isWaitlistedListing(listing);
  const contactCta = !availability.contact
    ? null
    : ({
        id: 'contact',
        label: isWaitlisted ? t('joinWaitlist') : recentlyContacted ? t('contactAgain') : t('message'),
        ariaLabel: isWaitlisted
          ? t('joinWaitlistAriaLabel')
          : recentlyContacted
            ? t('contactAgain')
            : t('messageProperty'),
        icon: <LucideIcon icon={Mail} size="sm" />,
        onClick: openContactForm,
        emphasis: 'filled',
        disabled: !identityResolved,
        ariaBusy: !identityResolved,
      } satisfies ListingCTA);

  const ctas: Array<ListingCTA> = contactCta ? [contactCta] : [];

  if (availability.tour) {
    ctas.push({
      id: 'tour',
      label: t('bookTour'),
      ariaLabel: t('bookTourAriaLabel'),
      icon: <LucideIcon icon={Calendar} size="sm" />,
      onClick: openTour,
      emphasis: 'outlined',
      disabled: !isTourReady || !availability.toursReady,
      ariaBusy: !isTourReady || !availability.toursReady,
    });
  }

  if (availability.apply) {
    ctas.push({
      id: 'apply',
      label: t('apply'),
      ariaLabel: t('applyNow'),
      icon: applyLink ? <LucideIcon icon={ExternalLink} size="sm" /> : null,
      href: applyLink?.href,
      onClick: applyLink?.onClick ?? openApply,
      emphasis: 'outlined',
    });
  }

  return { ctas, overlays };
};
